/**
 * Customer Export Action Handlers
 *
 * Handles customer data export operations in various formats (CSV, JSON, XLSX).
 * Supports both single and bulk customer exports.
 */

import { toast } from 'sonner';

import {
  CustomerData,
  ExportFormat,
} from '@/sections/customerManagement/types';

/**
 * Hook for customer export operations
 *
 * Provides export functionality for customers in multiple formats.
 * Generates downloadable files with customer data.
 *
 * @returns Object containing export handler functions
 *
 * @example
 * ```tsx
 * const { handleExportCustomers } = useCustomerExportHandlers();
 *
 * // Export as CSV
 * await handleExportCustomers(selectedCustomers, 'csv');
 *
 * // Export as JSON
 * await handleExportCustomers([customer], 'json');
 * ```
 */
export const useCustomerExportHandlers = () => {
  /**
   * Exports customer data to specified format
   *
   * Generates a downloadable file with customer information.
   * Supports CSV, JSON, and XLSX formats.
   *
   * @param customers - Customers to export
   * @param format - Export format ('csv' | 'json' | 'xlsx')
   */
  const handleExportCustomers = async (
    customers: CustomerData[],
    format: ExportFormat
  ) => {
    try {
      let content: string;
      let filename: string;
      let mimeType: string;

      switch (format) {
        case 'csv': {
          // Generate CSV content
          const headers = ['Email', 'Display Name', 'Type', 'Status', 'Health'];
          const rows = customers.map(c => [
            c.email,
            c.displayName || '',
            c.type || '',
            c.status || '',
            c.health || '',
          ]);

          content = [
            headers.join(','),
            ...rows.map(row =>
              row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
            ),
          ].join('\n');

          filename = `customers-${new Date().toISOString().split('T')[0]}.csv`;
          mimeType = 'text/csv';
          break;
        }

        case 'json': {
          // Generate JSON content
          const exportData = customers.map(c => ({
            email: c.email,
            displayName: c.displayName,
            type: c.type,
            status: c.status,
            health: c.health,
            critical: c.critical,
            high: c.high,
            medium: c.medium,
            low: c.low,
            info: c.info,
            exposure: c.exposure,
          }));

          content = JSON.stringify(exportData, null, 2);
          filename = `customers-${new Date().toISOString().split('T')[0]}.json`;
          mimeType = 'application/json';
          break;
        }

        case 'xlsx': {
          // For XLSX, we'll use CSV format as a fallback
          // In a production environment, you'd want to use a library like xlsx
          toast.info('XLSX export not yet implemented. Using CSV format.');

          const headers = ['Email', 'Display Name', 'Type', 'Status', 'Health'];
          const rows = customers.map(c => [
            c.email,
            c.displayName || '',
            c.type || '',
            c.status || '',
            c.health || '',
          ]);

          content = [
            headers.join(','),
            ...rows.map(row =>
              row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
            ),
          ].join('\n');

          filename = `customers-${new Date().toISOString().split('T')[0]}.csv`;
          mimeType = 'text/csv';
          break;
        }

        default:
          throw new Error(`Unsupported export format: ${format}`);
      }

      // Create download link
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(
        `Exported ${customers.length} customer${customers.length > 1 ? 's' : ''} as ${format.toUpperCase()}`
      );
    } catch (error) {
      console.error('[useCustomerExportHandlers] Export failed:', error);
      toast.error('Failed to export customers');
      throw error;
    }
  };

  return {
    handleExportCustomers,
  };
};
